import { useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";

export interface IInfoButtonProps {
	text: string;
}

type TTooltipPosition = {
	top: number;
	left: number;
	placement: "top" | "bottom";
};

export const InfoButton = ({ text }: IInfoButtonProps) => {
	const tooltipId = useId();
	const triggerRef = useRef<HTMLButtonElement>(null);
	const [hovered, setHovered] = useState(false);
	const [focused, setFocused] = useState(false);
	const [pinned, setPinned] = useState(false);
	const [position, setPosition] = useState<TTooltipPosition | null>(null);
	const visible = hovered || focused || pinned;

	useEffect(() => {
		if (!visible) return;

		const updatePosition = () => {
			const rect = triggerRef.current?.getBoundingClientRect();
			if (!rect) return;
			const tooltipHalfWidth = 150;
			const viewportPadding = 12;
			const left = Math.min(
				window.innerWidth - tooltipHalfWidth - viewportPadding,
				Math.max(tooltipHalfWidth + viewportPadding, rect.left + rect.width / 2),
			);
			const placement = rect.top > 90 ? "top" : "bottom";
			setPosition({
				left,
				top: placement === "top" ? rect.top - 8 : rect.bottom + 8,
				placement,
			});
		};

		const closeOnOutsideClick = (event: MouseEvent) => {
			if (!triggerRef.current?.contains(event.target as Node)) setPinned(false);
		};
		const closeOnEscape = (event: KeyboardEvent) => {
			if (event.key === "Escape") {
				setPinned(false);
				triggerRef.current?.blur();
			}
		};

		updatePosition();
		window.addEventListener("resize", updatePosition);
		window.addEventListener("scroll", updatePosition, true);
		document.addEventListener("mousedown", closeOnOutsideClick);
		document.addEventListener("keydown", closeOnEscape);
		return () => {
			window.removeEventListener("resize", updatePosition);
			window.removeEventListener("scroll", updatePosition, true);
			document.removeEventListener("mousedown", closeOnOutsideClick);
			document.removeEventListener("keydown", closeOnEscape);
		};
	}, [visible]);

	const tooltip =
		visible &&
		position &&
		typeof document !== "undefined" &&
		createPortal(
			<div
				id={tooltipId}
				role="tooltip"
				style={{
					position: "fixed",
					top: position.top,
					left: position.left,
					zIndex: 100002,
					width: "max-content",
					maxWidth: "300px",
					padding: "8px 10px",
					borderRadius: "6px",
					background: "rgba(24, 24, 27, 0.94)",
					boxShadow: "0 8px 24px rgba(0, 0, 0, 0.2)",
					color: "#ffffff",
					fontFamily: "inherit",
					fontSize: "12px",
					lineHeight: "17px",
					whiteSpace: "normal",
					pointerEvents: "none",
					transform: position.placement === "top" ? "translate(-50%, -100%)" : "translateX(-50%)",
				}}>
				{text}
			</div>,
			document.body,
		);

	return (
		<>
			<button
				ref={triggerRef}
				type="button"
				aria-label="Show field description"
				aria-describedby={visible ? tooltipId : undefined}
				aria-expanded={visible}
				onMouseEnter={() => setHovered(true)}
				onMouseLeave={() => setHovered(false)}
				onFocus={() => setFocused(true)}
				onBlur={() => {
					setFocused(false);
					setPinned(false);
				}}
				onClick={event => {
					event.preventDefault();
					event.stopPropagation();
					if (pinned) {
						setPinned(false);
						triggerRef.current?.blur();
					} else {
						setPinned(true);
					}
				}}
				style={{
					width: "18px",
					height: "18px",
					minWidth: "18px",
					flexShrink: 0,
					marginLeft: "auto",
					alignSelf: "center",
					padding: 0,
					border: "1px solid #a8a29e",
					borderRadius: "50%",
					background: "transparent",
					color: "#78716c",
					fontFamily: "inherit",
					fontSize: "11px",
					fontWeight: 600,
					lineHeight: "16px",
					cursor: "help",
				}}>
				?
			</button>
			{tooltip}
		</>
	);
};
